import { GoogleOAuthProvider } from '@react-oauth/google';
import { ConfirmationDialog, CustomTooltip, GoogleSignInButton, IGoogleLoginError, IGoogleLoginSuccess, IOptionType, MuiAutocompleteSelect, PillButton, ProjectDialog } from '@zz2/zz2-ui';
import React, { useMemo, useState } from 'react';
import { useAppDispatch } from '../../@types/redux';
import GeneralThunks from '../../store/general/thunk';
import { IconButton, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { IUser } from '../../@types/model/auth/user/user';
import { useHandleLinkGoogleAccount, useHandleUnlinkGoogleAccount } from '../../hooks/query/rights/userQueries';
import { AVAILABLE_DEFAULT_HOME_SCREENS } from '../../appConstants';
import * as localStorageService from '../../service/localStorage/localStorageService';

interface IBaseUserSettingsProps {
    isOpen : boolean;
    onClose : () => void;
    onLogout : () => void;
    currentUser : IUser | null;
}

const UserSettings = (props : IBaseUserSettingsProps) : React.ReactElement => {
    const selectedDefaultHome = localStorageService.getDefaultHome();
    const dispatch = useAppDispatch();

    const [unlinkEmailDialogOpen, setUnlinkEmailDialogOpen] = useState<boolean>(false);
    const [isLogOutDialogOpen, setIsLogOutDialogOpen] = useState<boolean>(false);

    const { isLoading: isUnlinkingEmail, mutate: unlinkEmail } = useHandleUnlinkGoogleAccount();
    const { isLoading: isLinkingGoogleAccount, mutate: linkGoogleAccount } = useHandleLinkGoogleAccount();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onGoogleAccountLinkSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        linkGoogleAccount(response.code);
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onGoogleAccountLinkFailure = (response : IGoogleLoginError) : void => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: response.error ?? 'Linking Error',
            ex: response,
        }));
    };

    const onUnlinkEmailClick = () => setUnlinkEmailDialogOpen(true);
    const onLogoutClick = () => setIsLogOutDialogOpen(true);

    const handleEmailUnlinking = () => {
        unlinkEmail();
        setUnlinkEmailDialogOpen(false);
    };

    const handleLogout = () => {
        props.onClose();
        props.onLogout();
    };

    const onCloseUserSettings = () : void => {
        props.onClose();
    };

    const onDefaultHomeChange = (selectedOption : IOptionType | null) : void => {
        localStorageService.setDefaultHome(selectedOption);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return isLinkingGoogleAccount
        || isUnlinkingEmail;
    }, [
        isLinkingGoogleAccount,
        isUnlinkingEmail,
    ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <ProjectDialog
                title={'User Settings'}
                isOpen={props.isOpen}
                maxWidth={'md'}
                isLoadingCircular={isLoading}
                onClose={onCloseUserSettings}>
                <div className={'w500 fdc aic jcc m20'}>
                    <div className={'fdr flx1 wfill aic'}>
                        <Typography className={'fdr flx1 m10'}>Default Home:</Typography>
                        <MuiAutocompleteSelect
                            className={'w250 mr10'}
                            name={'defaultHomeScreen'}
                            label={'Available Screens'}
                            options={AVAILABLE_DEFAULT_HOME_SCREENS}
                            value={selectedDefaultHome}
                            onChange={onDefaultHomeChange}
                        />
                    </div>
                    <div className={'fdc wfill mt10 mb10'}>
                        {
                            (!props.currentUser?.email || !props.currentUser.profilePictureURL)
                                && <div className={'zi1'}>
                                    <GoogleOAuthProvider clientId={OAUTH_CLIENT_ID}>
                                        <GoogleSignInButton
                                            className={'w300 mt10 mb20 h40'}
                                            onGoogleSignInSuccess={onGoogleAccountLinkSuccess}
                                            onGoogleSignInFailure={onGoogleAccountLinkFailure}
                                            buttonText={`Link ${(!!props.currentUser?.email && !props.currentUser.profilePictureURL) ? 'Profile Picture' : 'Google Account'}`}
                                        />
                                    </GoogleOAuthProvider>
                                </div>
                        }
                        {
                            !!props.currentUser?.email
                            && <div className={'fdr wfill jcsb mb10'}>
                                <Typography className={'fdr flx1 m10'}>Linked Email:</Typography>
                                <Typography variant={'body1'} className={'fdr fwb cpd m10'}>{ props.currentUser.email }</Typography>
                                <CustomTooltip title={'Unlink Email'}>
                                    <IconButton className={'cr'} onClick={onUnlinkEmailClick}>
                                        <ClearIcon />
                                    </IconButton>
                                </CustomTooltip>
                            </div>
                        }
                    </div>
                    <div className={'fdr aic pb10'}>
                        <PillButton
                            className={'h35 w100'}
                            text={'LOGOUT'}
                            color={'secondary'}
                            onClick={onLogoutClick}
                        />
                        <div className={'w100'}/>
                        <PillButton
                            className={'h35 w100'}
                            text={'CLOSE'}
                            color={'default'}
                            onClick={onCloseUserSettings}
                        />
                    </div>
                </div>
                <ConfirmationDialog
                    open={unlinkEmailDialogOpen}
                    title={'Unlink Email'}
                    isLoading={isUnlinkingEmail}
                    description={'Are you sure you would like to unlink the email address currently associated with your account?'}
                    onAccept={handleEmailUnlinking}
                    onClose={() => setUnlinkEmailDialogOpen(false)}
                    dialogType={'orange'}
                />
                <ConfirmationDialog
                    isLoading={false}
                    open={isLogOutDialogOpen}
                    title={'Log Out?'}
                    description={'Are you sure you would like to logout?'}
                    onAccept={handleLogout}
                    onClose={() => setIsLogOutDialogOpen(false)}
                    dialogType={'red'}
                />
            </ProjectDialog>
        </div>
    );
};

export default UserSettings;
